/**
 * Done. CPD — Dashboard Annual Plan (full Jo spec)
 *
 * Sections (when survey data is available):
 *   1. Progress bars (annual hours, 2-year cycle, AI drafts)
 *   2. Professional Profile Summary
 *   3. Annual CPD Rationale  + copy button
 *   4. Annual CPD Objectives
 *   5. AI-generated plan text (markdown)
 *   6. Monthly CPD Schedule (cards)
 *   7. Calendar Sync (per-event Google + .ics, bulk .ics)
 *   8. Download full plan (.txt)
 */

import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AILabel } from '@/components/ui/AILabel';
import { api } from '@/lib/api';
import { generateStructuredPlan } from '@/lib/planGenerator';
import { downloadIcsCalendar, downloadSingleIcs, downloadAnnualPlan } from '@/lib/downloads';
import type { CapStatus, AnnualPlan as AnnualPlanData, SurveyData } from '@/lib/types';

interface AnnualPlanProps {
  capStatus: CapStatus;
  annualPlan: AnnualPlanData | null;
}

interface BarRow {
  label: string;
  current: number;
  max: number;
}

function pct(current: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, Math.max(0, (current / max) * 100));
}

function ProgressRow({ row }: { row: BarRow }): React.ReactElement {
  const value = Math.round(pct(row.current, row.max));
  return (
    <div>
      <div className="flex justify-between mb-2">
        <span className="stitch-font-body font-medium text-[var(--stitch-primary)]">{row.label}</span>
        <span className="stitch-font-label text-[var(--stitch-surface-tint)]">{value}%</span>
      </div>
      <div className="w-full bg-[var(--stitch-surface-container)] h-1.5 rounded-full overflow-hidden">
        <div
          className="bg-[var(--stitch-surface-tint)] h-full rounded-full transition-[width] duration-700"
          style={{ width: `${String(value)}%` }}
        />
      </div>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <h4 className="stitch-font-label text-[11px] text-[var(--stitch-on-surface-variant)] tracking-[0.15em] uppercase mb-3">
      {children}
    </h4>
  );
}

function Divider(): React.ReactElement {
  return <hr className="border-[var(--stitch-surface-container-high)]" />;
}

export function AnnualPlan({ capStatus, annualPlan }: AnnualPlanProps): React.ReactElement {
  const [surveyData, setSurveyData] = useState<SurveyData | null>(null);
  const [copied, setCopied] = useState(false);
  const [icsDownloaded, setIcsDownloaded] = useState(false);
  const [calendarExpanded, setCalendarExpanded] = useState(false);

  useEffect(() => {
    if (!annualPlan) return;
    void (async () => {
      try {
        const data = await api.get<SurveyData | null>('/api/auth/survey-data');
        if (data) setSurveyData(data);
      } catch { /* calendar sync is progressive enhancement */ }
    })();
  }, [annualPlan]);

  const plan = useMemo(
    () => (surveyData ? generateStructuredPlan(surveyData) : null),
    [surveyData],
  );

  const year = annualPlan?.year ?? new Date().getFullYear();

  const rows: BarRow[] = [
    { label: 'Annual hours', current: capStatus.annualHoursLogged, max: capStatus.annualHourCap },
    { label: '2-year cycle', current: capStatus.cycleHoursLogged, max: capStatus.cycleHourCap },
    {
      label: 'AI drafts remaining',
      current: Math.max(0, capStatus.aiGenerationCap - capStatus.aiGenerationsUsed),
      max: capStatus.aiGenerationCap,
    },
  ];

  async function handleCopy(): Promise<void> {
    if (!plan) return;
    const text = [
      plan.rationale,
      '',
      'Objectives:',
      ...plan.objectives.map((o, i) => `${i + 1}. ${o}`),
    ].join('\n');
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => { setCopied(false); }, 2200);
  }

  function handleBulkIcs(): void {
    if (!plan) return;
    downloadIcsCalendar(plan.calendarEvents, surveyData?.reminders ?? []);
    setIcsDownloaded(true);
    setTimeout(() => { setIcsDownloaded(false); }, 2500);
  }

  const visibleEvents = calendarExpanded
    ? (plan?.calendarEvents ?? [])
    : (plan?.calendarEvents.slice(0, 4) ?? []);

  return (
    <div className="bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-surface-container-high)] p-8 rounded-xl stitch-editorial-shadow space-y-8">

      {/* ── Progress bars ── */}
      <div>
        <h3 className="stitch-font-label text-[13px] text-[var(--stitch-primary)] mb-6 tracking-[0.2em] uppercase">
          Annual Plan
        </h3>
        <div className="space-y-6">
          {rows.map(row => <ProgressRow key={row.label} row={row} />)}
        </div>
      </div>

      {/* ── Rules-based plan sections (need survey data) ── */}
      {plan && (
        <>
          <Divider />

          {/* Profile Summary */}
          <div>
            <SectionHeading>Professional Profile Summary</SectionHeading>
            <p className="text-sm text-[var(--stitch-on-surface-variant)] leading-relaxed">
              {plan.profileSummary}
            </p>
          </div>

          <Divider />

          {/* Rationale + copy */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <SectionHeading>Annual CPD Rationale</SectionHeading>
              <button
                type="button"
                onClick={() => { void handleCopy(); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--stitch-outline-variant)] bg-white hover:bg-[var(--stitch-surface-container-low)] stitch-font-label text-[11px] uppercase tracking-wider text-[var(--stitch-on-surface)] transition-colors"
              >
                <span className="material-symbols-outlined text-[15px]">
                  {copied ? 'check' : 'content_copy'}
                </span>
                {copied ? 'Copied!' : 'Copy Rationale & Objectives'}
              </button>
            </div>
            <p className="text-sm text-[var(--stitch-on-surface-variant)] leading-relaxed">
              {plan.rationale}
            </p>
          </div>

          <Divider />

          {/* Objectives */}
          <div>
            <SectionHeading>Annual CPD Objectives</SectionHeading>
            <ol className="space-y-2">
              {plan.objectives.map((obj, i) => (
                <li key={i} className="flex gap-3 text-sm text-[var(--stitch-on-surface-variant)] leading-relaxed">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-[var(--stitch-surface-container)] text-[var(--stitch-primary)] text-[11px] font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  {obj}
                </li>
              ))}
            </ol>
          </div>
        </>
      )}

      {/* ── AI plan text ── */}
      {annualPlan && (
        <>
          <Divider />
          <AiPlanSection annualPlan={annualPlan} />
        </>
      )}

      {/* ── Monthly schedule ── */}
      {plan && (
        <>
          <Divider />
          <div>
            <SectionHeading>Suggested Annual CPD Schedule</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(plan.modulesByMonth).map(([month, modules]) => (
                <div
                  key={month}
                  className="rounded-lg border border-[var(--stitch-surface-container-high)] bg-[var(--stitch-surface-container-lowest)] p-4 space-y-3"
                >
                  <p className="stitch-font-label text-[11px] text-[var(--stitch-primary)] tracking-[0.15em] uppercase font-semibold">
                    {month}
                  </p>
                  {modules.map(m => (
                    <div key={m.id} className="space-y-1">
                      <p className="text-sm font-semibold text-[var(--stitch-on-surface)]">{m.title}</p>
                      <p className="text-xs text-[var(--stitch-on-surface-variant)]">
                        {m.topicArea} · {m.format} · {m.estimatedMinutes} mins
                      </p>
                      <p className="text-xs text-[var(--stitch-on-surface-variant)] leading-relaxed">
                        {m.intendedOutcome}
                      </p>
                      <p className="text-xs text-[var(--stitch-surface-tint)]">
                        Evidence: {m.evidenceOutput}
                      </p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Calendar sync ── */}
      {plan && (
        <>
          <Divider />
          <div>
            <div className="flex items-start justify-between mb-3 gap-4">
              <SectionHeading>Monthly Calendar Sync</SectionHeading>
              {surveyData?.reminders && surveyData.reminders.length > 0 && (
                <p className="stitch-font-label text-[10px] text-[var(--stitch-on-surface-variant)] tracking-wide shrink-0">
                  Reminders: {surveyData.reminders.join(' · ')}
                </p>
              )}
            </div>

            <div className="space-y-3 mb-4">
              {visibleEvents.map(event => {
                const prefersIcs = surveyData?.preferredCalendar === 'Apple Calendar' || surveyData?.preferredCalendar === 'Outlook';
                const icsBtn = (
                  <button
                    key="ics"
                    type="button"
                    onClick={() => { downloadSingleIcs(event); }}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg stitch-font-label text-[11px] uppercase tracking-wider transition-colors ${
                      prefersIcs
                        ? 'border border-[var(--stitch-primary)] bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] hover:opacity-90'
                        : 'border border-[var(--stitch-outline-variant)] bg-white hover:bg-[var(--stitch-surface-container-low)] text-[var(--stitch-on-surface)]'
                    }`}
                  >
                    <span className="material-symbols-outlined text-[14px]">download</span>
                    {surveyData?.preferredCalendar === 'Outlook' ? 'Outlook' : 'Apple'} .ics
                  </button>
                );
                const googleBtn = (
                  <a
                    key="google"
                    href={event.googleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg stitch-font-label text-[11px] uppercase tracking-wider transition-colors ${
                      !prefersIcs
                        ? 'border border-[var(--stitch-primary)] bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] hover:opacity-90'
                        : 'border border-[var(--stitch-outline-variant)] bg-white hover:bg-[var(--stitch-surface-container-low)] text-[var(--stitch-on-surface)]'
                    }`}
                  >
                    <span className="material-symbols-outlined text-[14px]">calendar_add_on</span>
                    Google
                  </a>
                );
                return (
                  <div
                    key={`${event.module.id}-${event.start.toISOString()}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg border border-[var(--stitch-surface-container-high)] bg-white px-4 py-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-[var(--stitch-on-surface)]">
                        {event.module.month}: {event.module.title}
                      </p>
                      <p className="text-xs text-[var(--stitch-on-surface-variant)]">
                        {event.start.toLocaleString('en-NZ', { weekday: 'long', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {prefersIcs ? [icsBtn, googleBtn] : [googleBtn, icsBtn]}
                    </div>
                  </div>
                );
              })}
            </div>

            {plan.calendarEvents.length > 4 && (
              <button
                type="button"
                onClick={() => { setCalendarExpanded(v => !v); }}
                className="mb-4 stitch-font-label text-[12px] text-[var(--stitch-surface-tint)] hover:text-[var(--stitch-primary)] uppercase tracking-wider transition-colors"
              >
                {calendarExpanded ? 'Show less' : `Show all ${plan.calendarEvents.length} sessions`}
              </button>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleBulkIcs}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--stitch-outline-variant)] bg-white hover:bg-[var(--stitch-surface-container-low)] stitch-font-label text-[11px] uppercase tracking-wider text-[var(--stitch-on-surface)] transition-colors"
              >
                <span className="material-symbols-outlined text-[15px]">calendar_month</span>
                {icsDownloaded ? 'Downloaded!' : 'Download all sessions (.ics)'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Download full plan ── */}
      {plan && (
        <>
          <Divider />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => { downloadAnnualPlan(plan, year); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] stitch-font-label text-[12px] uppercase tracking-wider hover:opacity-90 transition-opacity"
            >
              <span className="material-symbols-outlined text-[16px]">download</span>
              Download Annual Plan
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── AI plan text section ──────────────────────────────────────────────────────

function AiPlanSection({ annualPlan }: { annualPlan: AnnualPlanData }): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="stitch-font-label text-[11px] text-[var(--stitch-on-surface-variant)] tracking-[0.15em] uppercase">
          AI Plan for {annualPlan.year}
        </span>
        {annualPlan.aiGenerated && <AILabel />}
      </div>
      <div className={expanded ? '' : 'line-clamp-6 overflow-hidden'}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => <h1 className="text-lg font-bold text-[var(--stitch-primary)] mt-6 mb-2 first:mt-0">{children}</h1>,
            h2: ({ children }) => <h2 className="text-base font-semibold text-[var(--stitch-primary)] mt-5 mb-2 first:mt-0">{children}</h2>,
            h3: ({ children }) => <h3 className="text-sm font-semibold text-[var(--stitch-on-surface)] mt-4 mb-1">{children}</h3>,
            p: ({ children }) => <p className="text-sm text-[var(--stitch-on-surface-variant)] leading-relaxed mb-3 last:mb-0">{children}</p>,
            ul: ({ children }) => <ul className="list-disc list-inside text-sm text-[var(--stitch-on-surface-variant)] mb-3 space-y-1">{children}</ul>,
            ol: ({ children }) => <ol className="list-decimal list-inside text-sm text-[var(--stitch-on-surface-variant)] mb-3 space-y-1">{children}</ol>,
            li: ({ children }) => <li className="leading-relaxed">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold text-[var(--stitch-on-surface)]">{children}</strong>,
            hr: () => <hr className="my-4 border-[var(--stitch-surface-container-high)]" />,
            table: ({ children }) => (
              <div className="overflow-x-auto my-4">
                <table className="w-full text-sm border-collapse">{children}</table>
              </div>
            ),
            thead: ({ children }) => <thead className="bg-[var(--stitch-surface-container-low)]">{children}</thead>,
            th: ({ children }) => <th className="text-left px-3 py-2 font-semibold text-[var(--stitch-on-surface)] border border-[var(--stitch-surface-container-high)] text-xs uppercase tracking-wide">{children}</th>,
            td: ({ children }) => <td className="px-3 py-2 text-[var(--stitch-on-surface-variant)] border border-[var(--stitch-surface-container-high)] align-top">{children}</td>,
            tr: ({ children }) => <tr className="even:bg-[var(--stitch-surface-container-lowest)]">{children}</tr>,
          }}
        >
          {annualPlan.planText}
        </ReactMarkdown>
      </div>
      <button
        type="button"
        onClick={() => { setExpanded(v => !v); }}
        className="mt-3 stitch-font-label text-[12px] text-[var(--stitch-surface-tint)] hover:text-[var(--stitch-primary)] uppercase tracking-wider transition-colors"
      >
        {expanded ? 'Show less' : 'Read full AI plan'}
      </button>
    </div>
  );
}
